import React, { useState, useCallback } from "react";
import "./Sidebar.css";
import { Thermometer, ThermometerSun, Droplets, Users, MapPin, Building2, AlertCircle, X } from "lucide-react";
import ShimmerLoader from "../ShimmerLoader/ShimmerLoader";

function ordinalSuffix(day) {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatDateOrdinal(dateStr) {
  // Parse as local date to avoid UTC-offset shifts on ISO strings without time
  const [year, month, day] = dateStr.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  const monthName = d.toLocaleString('en-GB', { month: 'long' });
  return `As on ${day}${ordinalSuffix(day)} ${monthName} ${year}`;
}

function getDateStaleness(dateStr) {
  if (!dateStr) return 'unavailable';
  const [y, m, d] = dateStr.split('-').map(Number);
  const dataDate = new Date(y, m - 1, d);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - dataDate) / 86400000);
  return diffDays > 1 ? 'stale' : 'ok';
}

function Sidebar({ selectedMarker, selectedFeature, setSelectedMarker, setSelectedFeature }) {
  const [isClosing, setIsClosing] = useState(false);

  // ✅ Close handler — triggers slide-out animation, then unmounts
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      if (selectedMarker) setSelectedMarker(null);
      if (selectedFeature) setSelectedFeature(null);
      setIsClosing(false);
    }, 300); // matches sidebarSlideOut animation duration
  }, [selectedMarker, selectedFeature, setSelectedMarker, setSelectedFeature]);

  // ✅ No sidebar if nothing selected
  if (!selectedMarker && !selectedFeature) return null;

  return (
    <div className={`sidebar-overlay ${isClosing ? "closing" : ""}`}>
      {/* ✅ Marker Sidebar Content */}
      {selectedMarker && (
        <>
          {/* Structured header — matches panel layout styling */}
          <div className="sidebar-header">
            <h2>{selectedMarker.name}</h2>
            <button className="sidebar-close-btn" onClick={handleClose} aria-label="Close sidebar">
              <X size={18} />
            </button>
          </div>

          {!selectedMarker.loading && !selectedMarker.error && (
            <div className="sidebar-date">
              {getDateStaleness(selectedMarker.date) === 'unavailable' ? (
                <>
                  <span className="sidebar-date-unavailable">Data unavailable</span>
                  <span className="sidebar-date-note">Climate data has not been recorded for this facility</span>
                </>
              ) : (
                <>
                  <span className="sidebar-date-text">{formatDateOrdinal(selectedMarker.date)}</span>
                  {getDateStaleness(selectedMarker.date) === 'stale' && (
                    <span className="sidebar-date-warning">Data may be outdated</span>
                  )}
                </>
              )}
            </div>
          )}

          {selectedMarker.parentCompany && (
            <p className="parent-company">{selectedMarker.parentCompany}</p>
          )}

          {/* Scrollable body */}
          <div className="sidebar-body">
            {/* Loading State - Shimmer */}
            {selectedMarker.loading && (
              <div className="shimmer-container">
                {/* Info Row Skeleton 1 */}
                <div className="info-row shimmer-row">
                  <ShimmerLoader width="20px" height="20px" borderRadius="4px" />
                  <div className="info-content" style={{ width: '100%' }}>
                    <ShimmerLoader width="60%" height="12px" style={{ marginBottom: '4px' }} />
                    <ShimmerLoader width="40%" height="14px" />
                  </div>
                </div>

                {/* Info Row Skeleton 2 */}
                <div className="info-row shimmer-row">
                  <ShimmerLoader width="20px" height="20px" borderRadius="4px" />
                  <div className="info-content" style={{ width: '100%' }}>
                    <ShimmerLoader width="70%" height="12px" style={{ marginBottom: '4px' }} />
                    <ShimmerLoader width="30%" height="14px" />
                  </div>
                </div>

                {/* Info Row Skeleton 3 */}
                <div className="info-row shimmer-row">
                  <ShimmerLoader width="20px" height="20px" borderRadius="4px" />
                  <div className="info-content" style={{ width: '100%' }}>
                    <ShimmerLoader width="50%" height="12px" style={{ marginBottom: '4px' }} />
                    <ShimmerLoader width="25%" height="14px" />
                  </div>
                </div>

                {/* Info Row Skeleton 4 */}
                <div className="info-row shimmer-row">
                  <ShimmerLoader width="20px" height="20px" borderRadius="4px" />
                  <div className="info-content" style={{ width: '100%' }}>
                    <ShimmerLoader width="65%" height="12px" style={{ marginBottom: '4px' }} />
                    <ShimmerLoader width="45%" height="14px" />
                  </div>
                </div>

                {/* Info Row Skeleton 5 */}
                <div className="info-row shimmer-row">
                  <ShimmerLoader width="20px" height="20px" borderRadius="4px" />
                  <div className="info-content" style={{ width: '100%' }}>
                    <ShimmerLoader width="40%" height="12px" style={{ marginBottom: '4px' }} />
                    <ShimmerLoader width="80%" height="14px" />
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {selectedMarker.error && (
              <div className="error-state">
                <AlertCircle size={40} className="error-icon" />
                <p>{selectedMarker.error}</p>
              </div>
            )}

            {/* Data Content - Only show when not loading and no error */}
            {!selectedMarker.loading && !selectedMarker.error && (
              <>
                <div className="info-row">
                  <Thermometer size={18} className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Minimum Temperature</span>
                    <span className="info-value">
                      {selectedMarker.tmin !== undefined ? `${selectedMarker.tmin}°C` : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="info-row">
                  <ThermometerSun size={18} className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Maximum Temperature</span>
                    <span className="info-value">
                      {selectedMarker.tmax !== undefined ? `${selectedMarker.tmax}°C` : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="info-row">
                  <Droplets size={18} className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Average Humidity</span>
                    <span className="info-value">
                      {selectedMarker.humidity !== undefined
                        ? `${(selectedMarker.humidity * 100).toFixed(0)}%`
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="info-row">
                  <Users size={18} className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Number of Workers</span>
                    <span className="info-value">
                      {selectedMarker.minWorkers && selectedMarker.maxWorkers
                        ? `${selectedMarker.minWorkers} - ${selectedMarker.maxWorkers}`
                        : selectedMarker.workers || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="info-row">
                  <MapPin size={18} className="info-icon" />
                  <div className="info-content">
                    <span className="info-label">Address</span>
                    <span className="info-value">{selectedMarker.address || 'N/A'}</span>
                  </div>
                </div>

                {selectedMarker.associatedCompanies && selectedMarker.associatedCompanies.length > 0 && (
                  <div className="companies-section">
                    <div className="section-header">
                      <Building2 size={18} className="info-icon" />
                      <span className="section-title">Associated Organizations</span>
                    </div>
                    <ul className="companies-list">
                      {selectedMarker.associatedCompanies.map((company, index) => (
                        <li key={index} className="company-item">{company}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Sidebar;
